import Spinner from '@components/spinner/Spinner';
import { useFetchTranscriptMetaAnalysis } from '@hooks/getQueries';
import { Box, Card, Chip, Divider, Stack, Typography } from '@mui/material';

const getStatusColor = (status = '') => {
  const normalized = status.toLowerCase();
  if (normalized === 'covered' || normalized === 'well-covered' || normalized === 'strong') {
    return 'success';
  }
  if (normalized === 'partial' || normalized === 'partially-covered' || normalized === 'adequate') {
    return 'warning';
  }
  if (
    normalized === 'not_covered' ||
    normalized === 'under-covered' ||
    normalized === 'needs_improvement'
  ) {
    return 'error';
  }
  return 'default';
};

const TranscriptMetaAnalysis = ({ transcriptId, enabled = true }) => {
  const { data: transcriptMetaAnalysis, isLoading } = useFetchTranscriptMetaAnalysis({
    transcriptId,
    enabled,
  });

  const metaAnalysis = transcriptMetaAnalysis?.meta_analysis;

  if (isLoading) {
    return (
      <Box
        p={4}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={2}
      >
        <Spinner />
        <Typography variant="body2" color="text.secondary">
          Generating transcript meta-analysis... This may take a few seconds.
        </Typography>
      </Box>
    );
  }

  if (!metaAnalysis) {
    return (
      <Box p={2} m={2} display="flex" justifyContent="center">
        Transcript meta-analysis is not available right now.
      </Box>
    );
  }

  return (
    <Stack spacing={2} mt={1}>
      <Card variant="outlined" sx={{ p: 0, pb: 2 }}>
        <Box display="flex" px={2} py={1.3} justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Session Assessment</Typography>
          <Chip
            label={metaAnalysis.completion_status || 'Assessment'}
            color={getStatusColor(metaAnalysis.completion_status)}
            size="small"
          />
        </Box>
        <Divider />
        <Box p={2}>
          <Typography>{metaAnalysis.overall_assessment}</Typography>
          <Typography mt={2} variant="subtitle2">
            Objective Alignment
          </Typography>
          <Typography mt={0.5}>{metaAnalysis.objective_alignment}</Typography>
          {metaAnalysis.metadata_alignment_note && (
            <>
              <Typography mt={2} variant="subtitle2">
                Setup Note
              </Typography>
              <Typography mt={0.5}>{metaAnalysis.metadata_alignment_note}</Typography>
            </>
          )}
        </Box>
      </Card>

      <Card variant="outlined" sx={{ p: 0, pb: 2 }}>
        <Box display="flex" px={2} py={1.3} justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Coverage</Typography>
        </Box>
        <Divider />
        <Stack spacing={2} p={2}>
          {metaAnalysis.coverage_items?.map((item, index) => (
            <Box
              key={`${item.label}-${index}`}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}
            >
              <Box
                display="flex"
                flexWrap="wrap"
                gap={1}
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle2">{item.label}</Typography>
                <Box display="flex" gap={1} alignItems="center">
                  <Chip label={item.source_type} size="small" variant="outlined" />
                  <Chip label={item.status} color={getStatusColor(item.status)} size="small" />
                </Box>
              </Box>
              <Typography mt={1}>{item.rationale}</Typography>

              {item.supporting_quotes?.length > 0 && (
                <Box mt={1.5}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Evidence
                  </Typography>
                  {item.supporting_quotes.map((quote, quoteIndex) => (
                    <Typography
                      key={`${quoteIndex}-${quote}`}
                      variant="body2"
                      fontStyle="italic"
                      mt={0.5}
                    >
                      {quote}
                    </Typography>
                  ))}
                </Box>
              )}

              {item.follow_up_opportunities?.length > 0 && (
                <Box mt={1.5}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Follow-up opportunities
                  </Typography>
                  {item.follow_up_opportunities.map((followUp, followUpIndex) => (
                    <Typography key={`${followUpIndex}-${followUp}`} variant="body2" mt={0.5}>
                      - {followUp}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      </Card>

      <Card variant="outlined" sx={{ p: 0, pb: 2 }}>
        <Box display="flex" px={2} py={1.3} justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Interviewer Performance</Typography>
        </Box>
        <Divider />
        <Stack spacing={2} p={2}>
          {metaAnalysis.interviewer_assessment?.map((item, index) => (
            <Box
              key={`${item.dimension}-${index}`}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}
            >
              <Box display="flex" justifyContent="space-between" gap={1} alignItems="center">
                <Typography variant="subtitle2">{item.dimension}</Typography>
                <Chip label={item.rating} color={getStatusColor(item.rating)} size="small" />
              </Box>
              <Typography mt={1}>{item.rationale}</Typography>
              {item.evidence_quotes?.length > 0 && (
                <Box mt={1.5}>
                  {item.evidence_quotes.map((quote, quoteIndex) => (
                    <Typography
                      key={`${quoteIndex}-${quote}`}
                      variant="body2"
                      fontStyle="italic"
                      mt={0.5}
                    >
                      {quote}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      </Card>

      <Card variant="outlined" sx={{ p: 0, pb: 2 }}>
        <Box display="flex" px={2} py={1.3} justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Strengths, Gaps, and Next Steps</Typography>
        </Box>
        <Divider />
        <Box p={2}>
          <Typography variant="subtitle2">Strengths</Typography>
          {metaAnalysis.strengths?.length > 0 ? (
            metaAnalysis.strengths.map((item, index) => (
              <Typography key={`${index}-${item}`} mt={0.5}>
                - {item}
              </Typography>
            ))
          ) : (
            <Typography mt={0.5} color="text.secondary">
              No strengths captured.
            </Typography>
          )}

          <Typography variant="subtitle2" mt={2.5}>
            Gaps
          </Typography>
          {metaAnalysis.gaps?.length > 0 ? (
            metaAnalysis.gaps.map((item, index) => (
              <Typography key={`${index}-${item}`} mt={0.5}>
                - {item}
              </Typography>
            ))
          ) : (
            <Typography mt={0.5} color="text.secondary">
              No major gaps captured.
            </Typography>
          )}

          <Typography variant="subtitle2" mt={2.5}>
            Recommended Follow-ups
          </Typography>
          {metaAnalysis.recommended_follow_ups?.length > 0 ? (
            metaAnalysis.recommended_follow_ups.map((item, index) => (
              <Typography key={`${index}-${item}`} mt={0.5}>
                - {item}
              </Typography>
            ))
          ) : (
            <Typography mt={0.5} color="text.secondary">
              No follow-ups recommended.
            </Typography>
          )}
        </Box>
      </Card>
    </Stack>
  );
};

export default TranscriptMetaAnalysis;
